<template>
    <div
      v-if="show"
      class="modal fixed inset-0 bg-opacity-85 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-[1000]"
      style="background-color: rgba(10, 25, 47, 0.85);"
      @click.self="closeModal"
      aria-modal="true"
      role="dialog"
      aria-labelledby="playerTeamModalTitle"
      @keydown.esc="closeModal"
    >
      <div class="modal-content relative mx-auto w-full max-w-lg">
        <div class="flex justify-between items-center pb-4 mb-4 border-b border-gray-700">
          <h3 id="playerTeamModalTitle" class="text-xl font-bold modal-title">{{ playerName }}</h3>
          <button
            class="close-modal"
            @click="closeModal"
            aria-label="Close modal"
          >&times;</button>
        </div>

        <div class="modal-body max-h-[78vh] overflow-y-auto pr-1">
          <div v-if="isLoading" class="text-center p-4">
            <p class="text-gray-600">Ladataan joukkueen tietoja...</p>
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mt-2"></div>
          </div>

          <div v-else-if="error" class="error-message p-3 bg-red-100 border border-red-400 rounded text-red-700 text-sm">
            Virhe joukkueen lataamisessa: {{ error }}
          </div>

          <div v-else-if="teamData">
            <div class="player-info-card mb-4">
              <h4 class="player-info__title">Tilastot</h4>
              <div class="player-info__grid">
                <div class="stat-item">
                  <span class="stat-label">Pisteet yhteensä</span>
                  <span class="stat-value">{{ teamData.totalPoints ?? 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Sijoitus</span>
                  <span class="stat-value">{{ playerRank !== null ? playerRank : 'Ladataan...' }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Vaihtoja jäljellä</span>
                  <span class="stat-value">{{ teamData.changesLeft ?? 'N/A' }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Rahaa jäljellä</span>
                  <div class="stat-with-image">
                    <span class="stat-value">{{ teamData.playerMoney?.toLocaleString() ?? 'N/A' }}</span>
                    <img src="@/assets/kolikot.webp" alt="Kolikot" class="stat-icon" />
                  </div>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Startit (hevoset)</span>
                  <span class="stat-value">{{ teamData.horseStarts ?? 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Voitot (hevoset)</span>
                  <span class="stat-value">{{ teamData.horseWins ?? 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Johtokerrat (hevoset)</span>
                  <span class="stat-value">{{ teamData.horseLeadCount ?? 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Voittosumma (hevoset)</span>
                  <span class="stat-value">{{ (teamData.horsePrizeSum ?? 0).toLocaleString() }} €</span>
                </div>
              </div>
              <DailyPointsChart
                :points-breakdown="teamData.pointsBreakdown"
                @view-horse="viewHorse"
                @view-jockey="viewJockey"
              />
              <button
                @click="togglePointsBreakdown"
                class="points-breakdown__toggle"
              >
                {{ showPointsBreakdown ? 'Piilota' : 'Näytä' }} pistesuoritukset
              </button>
              <div v-if="showPointsBreakdown" class="points-breakdown">
                    <ul v-if="teamData.pointsBreakdown && teamData.pointsBreakdown.length > 0" class="points-breakdown__list">
                      <li
                        v-for="(item, index) in teamData.pointsBreakdown"
                        :key="item.selectionKey"
                        :class="['points-breakdown__row', { 'date-separator-top': shouldShowDateSeparator(index) }]"
                      >
                        <span class="pb-left">
                          <span class="font-medium">{{ item.date }}:</span>
                          <span
                            :class="['breakdown-name', { 'breakdown-link': canViewDetails(item) }]"
                            @click="handleBreakdownClick(item)"
                            :title="canViewDetails(item) ? 'Näytä ' + item.name + ' suoritukset' : ''"
                          >
                            {{ item.name }}
                          </span>
                          ({{ item.type === 'Horse' ? 'Hevonen' : item.type === 'Jockey' ? 'Ohjastaja' : item.type }})
                        </span>
                        <span class="pb-right">{{ item.points ?? 0 }} p</span>
                      </li>
                    </ul>
                    <p v-else class="points-breakdown__empty">Ei pisteiden erittelyä saatavilla.</p>
                  </div>
            </div>

            <div class="flex flex-wrap gap-4 mb-4">
              <div class="selection flex-1 min-w-[200px]">
                <h4>Hevoset</h4>
                <div v-if="teamData.originalHorses && teamData.originalHorses.length > 0" class="list">
                  <div v-for="horse in teamData.originalHorses" :key="horse.horse_id" class="list-item horse-item">
                    <div class="modal-item-content">
                      <div class="modal-item-image">
                        <img src="@/assets/heppa2.webp" alt="Horse" class="modal-thumbnail" />
                      </div>
                      <div class="modal-item-text">
                        <h3
                          :class="{ 'cursor-pointer': horse.horse_id, 'opacity-50 cursor-default': !horse.horse_id }"
                          @click="horse.horse_id ? viewHorse(horse.horse_id, horse.horse_name) : null"
                          :title="horse.horse_id ? 'Näytä ' + horse.horse_name + ' suoritukset' : 'Suoritustietoja ei saatavilla'"
                        >
                          {{ horse.horse_name || 'N/A' }}
                        </h3>
                        <p v-if="formatHorseStartSummary(horse)" class="horse-start-summary">
                          {{ formatHorseStartSummary(horse) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="text-gray-500 italic text-center p-2">Ei valittuja hevosia.</p>
              </div>

              <div class="selection flex-1 min-w-[200px]">
                <h4>Ohjastaja</h4>
                <div v-if="teamData.originalJockey && teamData.originalJockey.jockey_id" class="list">
                  <div class="list-item jockey-item">
                    <div class="modal-item-content">
                      <div class="modal-item-image">
                        <img src="@/assets/jockey.webp" alt="Jockey" class="modal-thumbnail" />
                      </div>
                      <h3
                        :class="{ 'cursor-pointer': teamData.originalJockey.jockey_id, 'opacity-50 cursor-default': !teamData.originalJockey.jockey_id }"
                        @click="teamData.originalJockey.jockey_id ? viewJockey(teamData.originalJockey.jockey_id, teamData.originalJockey.jockey_name) : null"
                        :title="teamData.originalJockey.jockey_id ? 'Näytä ' + teamData.originalJockey.jockey_name + ' suoritukset' : 'Suoritustietoja ei saatavilla'"
                      >
                        {{ teamData.originalJockey.jockey_name || 'N/A' }}
                      </h3>
                    </div>
                  </div>
                </div>
                <p v-else class="text-gray-500 italic text-center p-2">Ei valittua ohjastajaa.</p>
              </div>
            </div>

            <div v-if="teamData.currentSelectionsVisible" class="day-changes-card">
              <h4>Päivän vaihdot</h4>
              <p class="day-changes-note">
                Vaihdot ovat näkyvissä, koska päivän ravit ovat jo alkaneet. Ne poistuvat tästä erittelystä pistepäivityksen jälkeen.
              </p>

              <div v-if="getAddedHorses().length || getRemovedHorses().length" class="day-change-row">
                <div v-if="getAddedHorses().length">
                  <strong>Lisätyt hevoset:</strong>
                  <span>{{ getAddedHorses().map(h => h.horse_name).join(', ') }}</span>
                </div>
                <div v-if="getRemovedHorses().length">
                  <strong>Poistetut hevoset:</strong>
                  <span>{{ getRemovedHorses().map(h => h.horse_name).join(', ') }}</span>
                </div>
              </div>

              <div v-if="isJockeyChanged()" class="day-change-row">
                <strong>Ohjastajavaihto:</strong>
                <span>
                  {{ teamData.originalJockey?.jockey_name || 'Ei aiempaa' }}
                  →
                  {{ teamData.currentJockey?.jockey_name || 'Ei valintaa' }}
                </span>
              </div>

              <div v-if="!getAddedHorses().length && !getRemovedHorses().length && !isJockeyChanged()" class="day-change-row">
                Päivälle on vahvistettu remmi, mutta siinä ei ole eroa alkuperäiseen valintaan.
              </div>
            </div>
          </div>

          <div v-else class="text-center text-gray-500 italic p-3 text-sm">
            Pelaajan tietoja ei saatavilla.
          </div>
        </div>

        <div class="confirmation-buttons pt-4 border-t mt-4 text-center">
          <button @click="closeModal" class="px-4 py-2 bg-accent text-dark-blue rounded hover:bg-accent-hover transition-all duration-200 font-medium">Sulje</button>
        </div>
      </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue';
import axios from '../plugins/axios'; // Ensure this path is correct
import DailyPointsChart from './DailyPointsChart.vue';

export default {
  name: 'PlayerTeamModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    playerId: {
      type: [String, Number], // PlayerId is passed as a prop
      default: null,
    },
    playerName: {
      type: String,
      default: 'Valittu pelaaja',
    },
  },
  emits: ['close', 'viewHorse', 'viewJockey'],
  components: {
    DailyPointsChart,
  },
  setup(props, { emit }) {
    const teamData = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const playerRank = ref(null); // Reactive variable for player rank
    const showPointsBreakdown = ref(false); // Control visibility of points breakdown
    const fetchTeamData = async () => {
      console.log("fetchTeamData called with playerId:", props.playerId);
      if (!props.playerId) {
        error.value = "Pelaajan ID puuttuu.";
        teamData.value = null;
        playerRank.value = null; // Reset rank
        isLoading.value = false;
        return;
      }
      isLoading.value = true;
      error.value = null;
      teamData.value = null;
      playerRank.value = null; // Reset rank at the beginning of fetch

      const playerPublicApiUrl = `${process.env.VUE_APP_API_BASE_URL}/player_public/${props.playerId}`;
      const playerRankApiUrl = `${process.env.VUE_APP_API_BASE_URL}/player_rank/${props.playerId}`;

      try {
        // Fetch main team data
        console.log(`Fetching public team data for ${props.playerId} from ${playerPublicApiUrl}`);
        const response = await axios.get(playerPublicApiUrl);
        if (response.data && response.data.originalHorses !== undefined && response.data.originalJockey !== undefined) {
            if (Array.isArray(response.data.pointsBreakdown)) {
              response.data.pointsBreakdown.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            } else {
              response.data.pointsBreakdown = [];
            }
          teamData.value = response.data;
          console.log("Public team data fetched:", teamData.value);

          // *** NEW: Fetch player rank after fetching team data ***
          try {
            console.log(`Fetching rank for player ${props.playerId} from ${playerRankApiUrl}`);
            const rankResponse = await axios.get(playerRankApiUrl);
            if (rankResponse.data && rankResponse.data.rank !== undefined) {
              playerRank.value = rankResponse.data.rank;
              console.log("Player rank fetched:", playerRank.value);
            } else {
              console.warn(`Rank not found in API response for player_rank for ${props.playerId}, or data is malformed:`, rankResponse.data);
              playerRank.value = '-'; // Player might not be ranked yet or other issue
            }
          } catch (rankError) {
            console.error(`Error fetching rank for player ${props.playerId}:`, rankError.response?.data || rankError.message);
            // A recently registered player may not exist in the latest snapshot yet.
            playerRank.value = '-';
          }

        } else {
          throw new Error("Virheellinen tietomuoto pelaajan joukkueelle.");
        }
      } catch (err) {
        console.error(`Error fetching team for player ${props.playerId}:`, err.response?.data || err.message);
        error.value = err.response?.data?.error || err.message || 'Pelaajan joukkueen tietoja ei voitu ladata.';
        teamData.value = null;
        playerRank.value = null; // Also clear rank on main data fetch error
      } finally {
        isLoading.value = false;
      }
    };

    const closeModal = () => {
      console.log("PlayerTeamModal: closeModal called");
      emit('close');
    };
    
    const togglePointsBreakdown = () => {
      showPointsBreakdown.value = !showPointsBreakdown.value;
    };

    const viewHorse = (horseId, horseName) => {
      emit('viewHorse', { horseId, horseName });
    };

    const viewJockey = (jockeyId, jockeyName) => {
      emit('viewJockey', { jockeyId, jockeyName });
    };

    const formatNextStart = (horse) => {
      const starts = Array.isArray(horse?.upcomingStarts) ? horse.upcomingStarts : [];
      if (starts.length === 0) {
        return '';
      }
      const start = starts[0];
      const date = start.displayDate || start.date || '';
      const venue = start.venue || '';
      const raceNumber = start.raceNumber ? `L${start.raceNumber}` : '';
      return [date, venue, raceNumber].filter(Boolean).join(' ');
    };

    const formatHorseStartSummary = (horse) => {
      const startsText = formatNextStart(horse);
      if (!startsText) {
        return '';
      }
      if (horse?.racing === 'today') {
        return `Tänään ${startsText}`;
      }
      if (horse?.racing === 'yes') {
        return `Startti ${startsText}`;
      }
      return startsText;
    };

    const currentHorseIds = () => new Set((teamData.value?.currentHorses || []).map(h => h.horse_id));
    const originalHorseIds = () => new Set((teamData.value?.originalHorses || []).map(h => h.horse_id));

    const getAddedHorses = () => {
      const originalIds = originalHorseIds();
      return (teamData.value?.currentHorses || []).filter(h => h.horse_id && !originalIds.has(h.horse_id));
    };

    const getRemovedHorses = () => {
      const currentIds = currentHorseIds();
      return (teamData.value?.originalHorses || []).filter(h => h.horse_id && !currentIds.has(h.horse_id));
    };

    const isJockeyChanged = () => {
      if (!teamData.value?.currentSelectionsVisible) {
        return false;
      }
      const originalId = teamData.value?.originalJockey?.jockey_id || null;
      const currentId = teamData.value?.currentJockey?.jockey_id || null;
      return originalId !== currentId;
    };

    const extractIdFromKey = (key) => {
      if (!key || typeof key !== 'string') {
          return null;
      }
      const parts = key.split('#');
      return parts.length > 0 ? parts[parts.length - 1] : null;
    };

    const canViewDetails = (item) => {
        return (item.type === 'Horse' || item.type === 'Jockey') && !!item.selectionKey;
    };

    const handleBreakdownClick = (item) => {
        if (!canViewDetails(item)) {
            return;
        }
        const id = extractIdFromKey(item.selectionKey);
        if (!id) {
            console.warn("Could not extract ID from breakdown item key:", item.selectionKey);
            return;
        }
        if (item.type === 'Horse') {
            viewHorse(id, item.name);
        } else if (item.type === 'Jockey') {
            viewJockey(id, item.name);
        }
    };

    const shouldShowDateSeparator = (index) => {
      if (index === 0) {
          return false;
      }
      if (!teamData.value?.pointsBreakdown?.length || index >= teamData.value.pointsBreakdown.length) {
            return false;
      }
      const currentItem = teamData.value.pointsBreakdown[index];
      const previousItem = teamData.value.pointsBreakdown[index - 1];
      return currentItem.date !== previousItem.date;
    };

    watch(() => props.show, (newValue) => {
      console.log("Modal show prop changed:", newValue, "playerId:", props.playerId);
      if (newValue && props.playerId) {
        fetchTeamData();
      } else if (!newValue) {
        // Clear data when modal is hidden
        teamData.value = null;
        error.value = null;
        isLoading.value = false;
        playerRank.value = null; // Reset rank when modal is closed
      }
    });

    watch(() => props.playerId, (newId, oldId) => {
        console.log("playerId prop changed from", oldId, "to", newId, "modal shown:", props.show);
        // Fetch data if playerId changes while modal is shown
        if (props.show && newId && newId !== oldId) {
            fetchTeamData();
        } else if (props.show && !newId) {
            // If playerId becomes null while modal is shown (e.g. parent component error)
            error.value = "Pelaajan ID ei ole enää saatavilla.";
            teamData.value = null;
            playerRank.value = null;
            isLoading.value = false;
        }
    });

    return {
      teamData,
      isLoading,
      error,
      playerRank,
      showPointsBreakdown,
      fetchTeamData,
      closeModal,
      viewHorse,
      viewJockey,
      getAddedHorses,
      getRemovedHorses,
      isJockeyChanged,
      canViewDetails,
      handleBreakdownClick,
      shouldShowDateSeparator,
      togglePointsBreakdown,
      formatHorseStartSummary,
    };
  },
};
</script>

<style scoped>
/* Styles specific to the PlayerTeamModal */

/* Basic spinner animation */
@keyframes spin {
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}

/* Modal Base Styling */
.modal-content {
  box-sizing: border-box;
  min-width: 0;
  max-height: 90vh; 
  display: flex; 
  flex-direction: column;
  background-color: #0a192f;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  color: #ccd6f6;
}
.modal-body {
  min-width: 0;
  overflow-y: auto; 
  -webkit-overflow-scrolling: touch; 
  overscroll-behavior: contain;
  padding: 0.5rem;
}
.close-modal { 
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  cursor: pointer;
  color: #8892b0;
  transition: color 0.3s ease;
}
.close-modal:hover {
  color: #64ffda;
}

/* Modal Title Styling */
.modal-title {
  color: #64ffda; /* Accent color from App.vue */
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Clickable names styling */
.opacity-50.cursor-default { color: #9ca3af; cursor: default; text-decoration: none; }

/* Player Info Card */
.player-info-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 1.5rem;
  background: #112240; /* Dark blue background to match theme */
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  color: #ccd6f6; /* Light text color from App.vue */
  border: 1px solid #1e3a5f;
}
.player-info__title {
  font-size: 1.5rem;
  font-weight: 700; 
  margin-bottom: 1.25rem; 
  color: #e6f1ff; /* Brighter text for title */
  position: relative;
  padding-bottom: 0.75rem;
}
.player-info__title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 4rem;
  height: 3px;
  background-color: #64ffda;
}
.player-info__grid {
  display: grid; 
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  margin-bottom: 1.25rem;
  min-width: 0;
}
.stat-item {
  box-sizing: border-box;
  min-width: 0;
  background: #1e3a5f; /* Darker blue background */
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex; 
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(100, 255, 218, 0.1);
}
.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px -12px rgba(0, 0, 0, 0.7);
  border-color: rgba(100, 255, 218, 0.3);
}
.stat-label {
  font-size: 0.875rem;
  color: #a8b2d1; /* Muted text color from App.vue */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: auto; 
  color: #e6f1ff; /* Brighter text for values */
}

/* Styling for stat with image */
.stat-with-image {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stat-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

@media (max-width: 768px) {
  .stat-icon {
    width: 25px;
    height: 25px;
  }
}

/* Points Breakdown Area */
details > summary { list-style: none; }
details > summary::-webkit-details-marker { display: none; }
details > summary::before {
  content: '+ '; 
  margin-right: 4px; 
  font-weight: bold; 
  display: inline-block;
  transition: transform 0.2s; 
  font-size: 0.9rem; 
  line-height: 1.25rem;
}
details[open] > summary::before { content: '- '; }
.points-breakdown__toggle {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: 5px;
  background: #64ffda;
  color: #0a192f;
  border: none;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  margin-bottom: 0.75rem;
  cursor: pointer;
  letter-spacing: 0.5px;
}
.points-breakdown__toggle:hover {
  background: #52d6c0;
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.25);
}
.points-breakdown {
  max-height: 24rem;
  overflow-y: auto;
  border-top: 1px solid #1e3a5f;
  padding-top: 1.25rem;
  margin-top: 0.5rem;
}
.points-breakdown__list { 
  list-style: none; 
  padding: 0; 
  margin: 0; 
}
.points-breakdown__row {
  display: flex; 
  justify-content: space-between; 
  gap: 1rem;
  padding: 0.75rem 0.5rem;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  transition: background-color 0.2s ease;
  color: #a8b2d1; /* Muted text color from App.vue */
}
.points-breakdown__row:hover {
  background-color: rgba(100, 255, 218, 0.05);
}
.points-breakdown__row:last-child { border-bottom: none; }
.pb-left { 
  flex-grow: 1; 
  word-break: break-word; 
}
.pb-right { 
  flex-shrink: 0; 
  font-weight: 600; 
  white-space: nowrap; 
  color: #64ffda;
}
.points-breakdown__empty {
  font-style: italic; 
  color: #8892b0; 
  padding: 1rem 0.5rem;
  font-size: 0.9rem;
}
.date-separator-top {
  border-top: 2px solid #1e3a5f; /* Darker border color */
  margin-top: 0.5rem;
  padding-top: 1rem;
}

/* Selection Cards (Horses/Jockey) */
.selection {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  transition: transform 0.3s ease;
}

.selection:hover {
  transform: translateY(-5px);
}

.selection h4 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  color: #e6f1ff;
  position: relative;
  padding-bottom: 0.75rem;
}

.selection h4::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 3rem;
  height: 3px;
  background-color: #64ffda;
}

.day-changes-card {
  background-color: #102a43;
  border: 1px solid rgba(100, 255, 218, 0.28);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #ccd6f6;
}

.day-changes-card h4 {
  color: #64ffda;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
}

.day-changes-note {
  color: #94a3b8;
  font-size: 0.86rem;
  margin-bottom: 0.75rem;
}

.day-change-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.55rem 0;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.day-change-row strong {
  color: #e6f1ff;
}

.list { 
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  list-style: none; 
  padding: 0; 
  margin: 0; 
}

.list-item {
  background-color: #1e3a5f;
  border: 1px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
  text-align: center;
}

.list-item:hover {
  border-color: rgba(100, 255, 218, 0.3);
  box-shadow: 0 8px 20px -12px rgba(0, 0, 0, 0.7);
}

.horse-item, .jockey-item {
  background-color: #1e3a5f;
}

.list-item {
  position: relative;
  padding-left: 55px !important;
}

.modal-item-content {
  display: flex;
  align-items: center;
}

.modal-item-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.modal-item-image {
  width: 40px;
  height: 40px;
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  border-radius: 4px;
}

.modal-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.list-item h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #e6f1ff;
  line-height: 1.4;
}

.list-item h3.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.list-item h3.cursor-pointer:hover {
  color: #64ffda;
  text-decoration: underline;
}

.horse-start-summary {
  color: #94a3b8;
  font-size: 0.78rem;
  line-height: 1.2;
  margin: 0.15rem 0 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.breakdown-name {
  display: inline;
}
.breakdown-link {
  color: #64ffda;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  margin: 0 2px;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  position: relative;
  display: inline-flex;
  align-items: center;
}
.breakdown-link:hover {
  color: #52d6c0;
  background-color: rgba(100, 255, 218, 0.1);
  text-decoration: underline;
}
.points-breakdown__row { /* Base color for row text */
  color: #a8b2d1; /* Muted text color from App.vue */
}

/* Add info icon for breakdown links */
.breakdown-link::after {
  content: 'ℹ️';
  font-size: 0.75rem;
  opacity: 0.7;
  margin-left: 0.25rem;
}

/* Scrollbar styling */
.modal-body::-webkit-scrollbar { width: 6px; }
.modal-body::-webkit-scrollbar-track { background: #0a192f; border-radius: 3px; }
.modal-body::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 3px; }
.modal-body::-webkit-scrollbar-thumb:hover { background: #2d4a6d; }

.points-breakdown::-webkit-scrollbar { width: 4px; }
.points-breakdown::-webkit-scrollbar-track { background: #112240; border-radius: 2px; }
.points-breakdown::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 2px; }
.points-breakdown::-webkit-scrollbar-thumb:hover { background: #2d4a6d; }

/* Button accent colors */
.bg-accent {
  background-color: #64ffda; /* Accent color from App.vue */
}
.bg-accent-hover {
  background-color: #52d6c0; /* Darker accent color on hover */
}
.text-dark-blue {
  color: #0a192f; /* Dark blue text for contrast on accent background */
}
.text-accent {
  color: #64ffda; /* Accent color from App.vue */
}
.border-accent {
  border-color: #64ffda; /* Accent color from App.vue */
}

/* Improved button styling */
button {
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
}

button.bg-accent {
  background-color: #64ffda;
  color: #0a192f;
}

button.bg-accent:hover {
  background-color: #52d6c0;
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .player-info-card {
    padding: 1.25rem;
  }
  
  .player-info__grid {
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .stat-item {
    padding: 0.75rem 1rem;
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
  
  .selection {
    padding: 1.25rem;
  }
  
  .selection h4 {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .modal-content {
    padding: 1rem;
  }

  .modal-body {
    padding: 0.25rem;
  }

  .player-info-card {
    padding: 1rem;
  }

  .player-info__grid {
    gap: 0.75rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat-item {
    padding: 0.7rem;
  }

  .stat-label {
    font-size: 0.72rem;
    letter-spacing: 0.25px;
    overflow-wrap: anywhere;
  }

  .stat-value {
    font-size: 1.1rem;
    overflow-wrap: anywhere;
  }

  .stat-with-image {
    gap: 6px;
    min-width: 0;
  }
  
  .points-breakdown__toggle {
    width: 100%;
    text-align: center;
    padding: 0.6rem 1rem;
  }
}

@media (max-width: 320px) {
  .player-info__grid {
    grid-template-columns: 1fr;
  }
}
</style>
